import { useEffect, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';

import { BrowseResultItem } from '@cartwhl/common/types';
import LazyVideo from '@cartwhl/ui/atoms/LazyVideo';
import Loading from '@cartwhl/ui/atoms/Loading';
import Text from '@cartwhl/ui/base/Text';

import useInfiniteScroll from 'hooks/useInfiniteScroll';
import { browseMotionStore, userStore } from 'stores';
import { getBrowseViewPageSize } from 'stores/browseMotionStore';

import { BROWSE_DESKTOP_MIN_WIDTH } from '../constants';
import { useLibraryStore } from '../useLibraryStore';
import BrowseGif from './BrowseGif';
import BrowseHeartButton from './BrowseHeartButton';
import BrowseMotionFeedbackMenu from './BrowseMotionFeedbackMenu';
import InternalReviewIcons from './InternalReviewIcons';

const CHUNK_BUFFER_SIZE = 2; // Number of chunks to render above/below viewport

type Props = {
  activeTags: string[];
  category: string;
  onBrowseChoice: (video: BrowseResultItem) => void;
  subcategory: string;
};

const getPageSize = (width: number) => {
  if (width >= 3400) return 8;
  if (width >= 2800) return 7;
  if (width >= 2400) return 6;
  if (width >= 2200) return 5;
  if (width >= 900) return 4;
  return 4;
};

export default function BrowseSearchResults({
  activeTags,
  category,
  onBrowseChoice,
  subcategory,
}: Props) {
  const catPlusSubCat = `${category}.${subcategory}`;
  const { favoritePages, pages, selectedView, hasMore, loading, reviewMode } =
    useSnapshot(browseMotionStore.state);
  const { isCartwheeler } = useSnapshot(userStore.state);
  const { favorites, flagged, addToFlagged, hasFootContacts } =
    useLibraryStore();
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 3 });
  const [containerHeight, setContainerHeight] = useState(0);
  const lastRangeRef = useRef(visibleRange);
  const [pageSize, setPageSize] = useState(getBrowseViewPageSize(4));
  const [modalWidth, setModalWidth] = useState(BROWSE_DESKTOP_MIN_WIDTH); // Default modal width
  const pagesToUse = selectedView === 'favorites' ? favoritePages : pages;
  const allItems = pagesToUse.flatMap((page) => page.items);
  const filteredItems = allItems.filter(
    (item) =>
      !flagged.has(item.jobID) &&
      (reviewMode ? !hasFootContacts(item.jobID) : true),
  );

  // Create chunks
  const itemsPerChunk = pageSize;
  let chunkIndex = 0;
  const videoChunks = filteredItems.reduce((chunks, item) => {
    if (!chunks[chunkIndex]) {
      chunks[chunkIndex] = [];
    }

    if (
      (category === '' && subcategory === '') ||
      item.categoryAndSubcategories.includes(catPlusSubCat)
    ) {
      if (
        activeTags.length > 0 &&
        !activeTags.some((tag) => item.tags.includes(tag))
      ) {
        // Skip if doesn't match tags
        return chunks;
      }

      chunks[chunkIndex].push(item);
      if (chunks[chunkIndex].length === itemsPerChunk) {
        chunkIndex++;
      }
    }
    return chunks;
  }, [] as BrowseResultItem[][]);

  // Update container height on mount and resize
  useEffect(() => {
    const updateContainerHeight = () => {
      if (containerRef.current) {
        // Get the parent container that has the scroll
        const scrollContainer = containerRef.current.closest('.overflow-auto');
        if (scrollContainer) {
          setContainerHeight(scrollContainer.clientHeight);
        }
      }
    };

    // Use a small timeout to ensure the container is rendered
    const timeoutId = setTimeout(updateContainerHeight, 0);
    window.addEventListener('resize', updateContainerHeight);
    return () => {
      window.removeEventListener('resize', updateContainerHeight);
      clearTimeout(timeoutId);
    };
  }, []);

  // Update visible chunks based on scroll position
  useEffect(() => {
    const updateVisibleChunks = () => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      const scrollContainer = container.closest('.overflow-auto');
      if (!scrollContainer) return;

      // Get the first chunk's height including its divider
      const firstChunk = container.children[0] as HTMLElement;
      if (!firstChunk) return;

      const chunkHeight = firstChunk.offsetHeight;
      const scrollTop = scrollContainer.scrollTop;

      // Calculate which chunks should be visible based on container scroll
      // TODO: Figure out better logic here, it resulted in too much popping when scrolling.
      // so zeroing out for now.
      // const estimatedStartChunk = Math.max(
      //   0,
      //   Math.floor(scrollTop / chunkHeight) - CHUNK_BUFFER_SIZE,
      // );
      const estimatedStartChunk = 0;
      const estimatedEndChunk = Math.min(
        videoChunks.length,
        Math.ceil((scrollTop + scrollContainer.clientHeight) / chunkHeight) +
          CHUNK_BUFFER_SIZE,
      );

      // Only update if the range has changed significantly
      const rangeChanged =
        Math.abs(estimatedStartChunk - lastRangeRef.current.start) > 1 ||
        Math.abs(estimatedEndChunk - lastRangeRef.current.end) > 1;

      if (rangeChanged) {
        const newRange = {
          start: estimatedStartChunk,
          end: estimatedEndChunk,
        };
        lastRangeRef.current = newRange;
        setVisibleRange(newRange);
      }

      // Check if we're near the bottom and should load more
      const distanceFromBottom =
        scrollContainer.scrollHeight -
        (scrollTop + scrollContainer.clientHeight);

      // Load more if we're within 3 chunk heights of the bottom
      if (
        distanceFromBottom < chunkHeight * 3 &&
        hasMore &&
        !loading.nextPage &&
        !loading.data
      ) {
        browseMotionStore.loadNextPage();
      }
    };

    // Debounce the update to prevent rapid calls
    let timeoutId: number | undefined;
    const debouncedUpdate = () => {
      if (timeoutId) {
        window.cancelAnimationFrame(timeoutId);
      }
      timeoutId = window.requestAnimationFrame(updateVisibleChunks);
    };

    const scrollContainer = containerRef.current?.closest('.overflow-auto');
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', debouncedUpdate);
      scrollContainer.addEventListener('wheel', debouncedUpdate);
    }
    window.addEventListener('resize', debouncedUpdate);

    // Initial update
    debouncedUpdate();

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', debouncedUpdate);
        scrollContainer.removeEventListener('wheel', debouncedUpdate);
      }
      window.removeEventListener('resize', debouncedUpdate);
      if (timeoutId) {
        window.cancelAnimationFrame(timeoutId);
      }
    };
  }, [
    videoChunks.length,
    hasMore,
    loading.nextPage,
    loading.data,
    containerHeight,
  ]);

  useEffect(() => {
    const updatePageSize = () => {
      const columns = getPageSize(window.innerWidth);
      setPageSize(getBrowseViewPageSize(columns));
    };

    window.addEventListener('resize', updatePageSize);
    updatePageSize();

    return () => window.removeEventListener('resize', updatePageSize);
  }, []);

  // Update modal width when container changes
  useEffect(() => {
    const updateModalWidth = () => {
      if (containerRef.current) {
        const modalElement = containerRef.current.closest(
          '[data-radix-dialog-content]',
        );

        if (modalElement) {
          setModalWidth(modalElement.clientWidth);
        } else {
          setModalWidth(BROWSE_DESKTOP_MIN_WIDTH);
        }
      }
    };

    window.addEventListener('resize', updateModalWidth);
    updateModalWidth();
    return () => {
      window.removeEventListener('resize', updateModalWidth);
    };
  }, []);

  const sentinelRef = useInfiniteScroll({
    onIntersect: () => browseMotionStore.loadNextPage(),
    enabled: hasMore && !loading.nextPage && !loading.data,
  });

  return (
    <div ref={containerRef} className="p-4">
      {/* Render only chunks within the visible range */}
      {videoChunks
        .slice(visibleRange.start, visibleRange.end)
        .map((chunk, virtualIndex) => {
          const actualIndex = virtualIndex + visibleRange.start;
          return (
            <div key={actualIndex}>
              <div className="grid grid-cols-2 gap-x-4 gap-y-11 min-[900px]:grid-cols-4 min-[2200px]:grid-cols-5 min-[2400px]:grid-cols-6 min-[2800px]:grid-cols-7 min-[3400px]:grid-cols-8">
                {chunk.map((video, i) => (
                  <div
                    key={`${video.jobID}-${actualIndex}-${i}`}
                    className="group relative flex cursor-pointer flex-col"
                    onClick={() => onBrowseChoice(video)}
                  >
                    <div className="pointer-events-none absolute top-2 left-2 z-10 flex justify-end">
                      <div
                        className={`pointer-events-auto transition-opacity group-hover:opacity-100 ${
                          favorites.has(video.jobID)
                            ? 'md:opacity-100'
                            : 'md:opacity-0'
                        }`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <BrowseHeartButton
                          jobID={video.jobID}
                          title={video.title}
                        />
                      </div>
                      <div
                        className={`pointer-events-auto hidden gap-10 pl-10 transition-opacity group-hover:opacity-100 sm:flex md:gap-2 md:pl-2 md:opacity-0`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {isCartwheeler && (
                          <InternalReviewIcons
                            video={video}
                            category={category}
                            subcategory={subcategory}
                            addToFlagged={addToFlagged}
                          />
                        )}
                      </div>
                    </div>
                    <div className="pointer-events-none absolute top-2 right-2 left-2 z-10 hidden justify-end md:flex">
                      <div
                        className="pointer-events-auto opacity-0 transition-opacity group-hover:opacity-100 data-[state=open]:opacity-100"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <BrowseMotionFeedbackMenu
                          video={video}
                          onBrowseChoice={onBrowseChoice}
                        />
                      </div>
                    </div>
                    <div className="mx-auto w-full">
                      {video.gifPathV2 ? (
                        <div className="aspect-square w-full">
                          <BrowseGif src={video.gifPathV2} contained={false} />
                        </div>
                      ) : (
                        <LazyVideo
                          className="aspect-square w-full rounded-lg"
                          src={video.mp4PathV2}
                          fallbackSrcs={[video.mp4Path]}
                          mode="autoplay"
                          contained={false}
                        />
                      )}
                    </div>
                    <Text
                      variant="studio/body/medium/sm"
                      className="mt-3 text-left"
                    >
                      {video.title}
                    </Text>
                  </div>
                ))}
              </div>
              {actualIndex < videoChunks.length - 1 && (
                <div className="-mx-4 my-12 border-t" />
              )}
            </div>
          );
        })}

      {(hasMore || loading.nextPage) && (
        <div
          ref={sentinelRef}
          className="mt-4 flex justify-center border-t pt-4"
        >
          {loading.nextPage ? (
            <Loading className="h-6 w-6" />
          ) : (
            <Text variant="studio/body/sm">Loading more results...</Text>
          )}
        </div>
      )}

      {!hasMore && allItems.length > 0 && (
        <div className="border-gray-30 dark:border-gray-60 mt-4 flex justify-center border-t pt-4">
          <Text variant="studio/body/sm">More motions coming soon!</Text>
        </div>
      )}
    </div>
  );
}
